import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { SectionHeader } from "@/components/common/Widgets";
import { fmtDate } from "@/lib/format";
import { ArrowRight, CheckCircle2, XCircle, RotateCcw } from "lucide-react";

const ACTIONS = [
  { code: "APPROVE", label: "Approve", icon: CheckCircle2, color: "text-success" },
  { code: "FORWARD", label: "Forward", icon: ArrowRight, color: "text-accent" },
  { code: "SEND_BACK", label: "Send Back", icon: RotateCcw, color: "text-warning" },
  { code: "REJECT", label: "Reject", icon: XCircle, color: "text-destructive" },
];
const ROLES = ["FO", "BM", "CO", "RO", "HO"];

export default function WorkflowSection({ app, approvals, audit, reload }) {
  const { t } = useTranslation();
  const [action, setAction] = useState("FORWARD");
  const [nextRole, setNextRole] = useState("CO");
  const [comment, setComment] = useState("");
  const [deviation, setDeviation] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!comment) return toast.error("Add a comment for audit trail");
    setBusy(true);
    try {
      await api.post("/workflow/action", { application_id: app.id, action, next_role: nextRole || null, comment, deviation_noted: deviation || null });
      toast.success(`Action recorded: ${action}`);
      setComment(""); setDeviation("");
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <SectionHeader title={t("tabs.workflowTitle", "Workflow & Approvals")} subtitle={t("tabs.workflowSubtitle", "Multi-level routing with mandatory comments for audit.")} />

      <Card className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Row label={t("tabs.workflowMoveTo", "Action")}>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger data-testid="workflow-action-select"><SelectValue /></SelectTrigger>
            <SelectContent>{ACTIONS.map(a => <SelectItem key={a.code} value={a.code}>{t(`tabs.workflow${a.code.charAt(0)}${a.code.slice(1).toLowerCase()}`, a.label)}</SelectItem>)}</SelectContent>
          </Select>
        </Row>
        {action !== "REJECT" && (
          <Row label={t("tabs.workflowMoveTo", "Forward to Role")}>
            <Select value={nextRole} onValueChange={setNextRole}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </Row>
        )}
        <Row label="Deviation Noted (optional)"><Textarea rows={1} value={deviation} onChange={(e) => setDeviation(e.target.value)} placeholder="e.g., DSCR < 1.5 — conditional approval" /></Row>
        <div className="md:col-span-3">
          <Row label={`${t("tabs.workflowRemarks", "Comment")} (${t("forms.required", "required")})`}><Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="workflow-comment-input" /></Row>
        </div>
        <div className="md:col-span-3 flex justify-end"><Button onClick={submit} disabled={busy} data-testid="workflow-submit-button">{t("tabs.workflowSubmitForApproval", "Record Action")}</Button></div>
      </Card>

      <Card className="p-4">
        <SectionHeader title="Approval Timeline" subtitle={`${approvals.length} approval action(s)`} />
        {approvals.length === 0 && <div className="text-sm text-muted-foreground">No approvals recorded yet.</div>}
        <ol className="relative border-l border-border ml-3 mt-1 space-y-3">
          {approvals.map((p) => {
            const a = ACTIONS.find(x => x.code === p.action) || ACTIONS[1];
            return (
              <li key={p.id} className="ml-4">
                <div className="absolute -left-2.5 mt-1"><div className={`h-3 w-3 rounded-full bg-card border-2 ${a.color.includes('success') ? 'border-success' : a.color.includes('destructive') ? 'border-destructive' : a.color.includes('warning') ? 'border-warning' : 'border-accent'}`} /></div>
                <div className="flex flex-wrap items-baseline gap-2">
                  <Badge variant="outline" className="mono text-[10px]">{p.action}</Badge>
                  <span className="font-medium">{p.actor_name}</span>
                  <span className="text-xs text-muted-foreground">({p.actor_role}) {p.next_role && <>→ <span className="font-medium">{p.next_role}</span></>}</span>
                  <span className="text-xs text-muted-foreground mono ml-auto">{fmtDate(p.timestamp)}</span>
                </div>
                {p.comment && <div className="text-sm mt-1">{p.comment}</div>}
                {p.deviation_noted && <div className="text-xs mt-1 text-warning">Deviation: {p.deviation_noted}</div>}
              </li>
            );
          })}
        </ol>
      </Card>

      <Card className="p-4">
        <SectionHeader title={t("tabs.workflowAuditTrail", "Audit Trail")} subtitle="Full immutable activity log" />
        <ul className="space-y-2.5">
          {(audit || []).map((e) => (
            <li key={e.id} className="text-sm flex items-start gap-2.5">
              <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-accent" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-medium">{e.actor_name}</span>
                  <span className="text-xs text-muted-foreground">({e.actor_role})</span>
                  <span className="text-xs text-accent mono">{e.action}</span>
                  <span className="text-[11px] text-muted-foreground mono ml-auto">{fmtDate(e.timestamp)}</span>
                </div>
                {e.details && Object.keys(e.details).length > 0 && <div className="text-xs text-muted-foreground mt-0.5 mono">{JSON.stringify(e.details)}</div>}
              </div>
            </li>
          ))}
          {(audit || []).length === 0 && <li className="text-sm text-muted-foreground">No audit entries.</li>}
        </ul>
      </Card>
    </div>
  );
}
function Row({ label, children }) { return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</Label>{children}</div>); }
